import { createSlice } from '@reduxjs/toolkit';
import {
  calendarV2OptInChanged,
  initializeSuccess,
  receiveAvatarUrl,
  setContractDetails,
} from '../actions';
import type { ContractDetails } from '../actions';
import type { MyAccountData } from '../shared/types';

export interface CurrentAccountState {
  id: string | null;
  displayName: string;
  locale: string;
  email: string;
  roles: MyAccountData['roles'];
  avatarUrl: string | null;
  firstName: string;
  lastName: string;
  color: string | null;
  organizationAdmin: boolean;
  numberOfMerchants: number;
  isKeyAccount: boolean;
  isTestAccount: boolean;
  currentSigninAt: string;
  contractDetails: {
    packages: string[];
    states: string[];
    paymentSources: string[];
    // undefined before setContractDetails is first dispatched; string | null thereafter
    defaultPaymentSourceType:
      | ContractDetails['defaultPaymentSourceType']
      | undefined;
  };
  organisationName: string;
  organizationId: string;
  calendarV2OptIn: boolean;
}

const initialState: CurrentAccountState = {
  id: null,
  displayName: '',
  locale: 'en',
  email: '',
  roles: [],
  avatarUrl: null,
  firstName: '',
  lastName: '',
  color: null,
  organizationAdmin: false,
  numberOfMerchants: 0,
  isKeyAccount: false,
  isTestAccount: false,
  currentSigninAt: '',
  contractDetails: {
    defaultPaymentSourceType: undefined,
    packages: [],
    paymentSources: [],
    states: [],
  },
  organisationName: '',
  organizationId: '',
  calendarV2OptIn: false,
};

const currentAccountSlice = createSlice({
  name: 'currentAccount',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(receiveAvatarUrl, (state, action) => {
        state.avatarUrl = action.payload;
      })
      .addCase(setContractDetails, (state, action) => {
        state.contractDetails = action.payload;
      })
      .addCase(calendarV2OptInChanged, (state, action) => {
        state.calendarV2OptIn = action.payload;
      })
      .addCase(initializeSuccess, (state, action) => {
        const {
          id,
          locale,
          display_name: displayName,
          roles,
          email,
          employee: { color, given_name: firstName, surname: lastName },
          organization_admin: organizationAdmin,
          merchants,
          organization: {
            id: organizationId,
            key_account_contract: isKeyAccount,
            test: isTestAccount,
            name: organisationName,
          },
          current_sign_in_at: currentSigninAt,
        } = action.payload.myAccountData;

        return {
          ...state,
          id,
          locale,
          displayName,
          roles,
          email,
          firstName: firstName ?? '',
          lastName,
          color,
          organizationAdmin,
          numberOfMerchants: merchants.length,
          isKeyAccount,
          isTestAccount,
          currentSigninAt,
          organisationName,
          organizationId,
          calendarV2OptIn: action.payload.calendarV2OptIn,
        };
      });
  },
});

export const currentAccountReducer = currentAccountSlice.reducer;
